/**
 * src/components/DurationChart.js
 * Duration / SLA visualization — summary bars with threshold markers, or histogram mode.
 */

import {
  ResponsiveContainer,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine
} from 'recharts';

function formatDuration(seconds) {
  if (seconds == null) return '—';
  const s = Math.round(seconds);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0 || parts.length === 0) parts.push(`${mins}m`);
  return parts.join(' ');
}

function getBarColor(medianSeconds, sla) {
  if (!sla) return 'var(--accent)';
  if (sla.breach_seconds && medianSeconds >= sla.breach_seconds) return 'var(--danger, #ef4444)';
  if (sla.warning_seconds && medianSeconds >= sla.warning_seconds) return 'var(--warning, #f59e0b)';
  return 'var(--success, #22c55e)';
}

function getComplianceBadge(sla) {
  if (!sla || sla.within_breach_pct == null) return null;
  const pct = sla.within_breach_pct;
  const color = pct >= 90 ? 'var(--success, #22c55e)' : pct >= 70 ? 'var(--warning, #f59e0b)' : 'var(--danger, #ef4444)';
  return { pct, color };
}

/** Summary bars mode — one bar per transition */
function SummaryBars({ transitions, onSelect, selectedIndex, compact }) {
  if (!transitions || transitions.length === 0) return null;

  const maxMedian = Math.max(...transitions.map(t => {
    const p50 = t.stats?.percentiles?.p50?.seconds;
    return p50 || t.stats?.avg_seconds || 0;
  }), 1);

  return (
    <div className="duration-summary">
      {transitions.map((t, i) => {
        const medianSeconds = t.stats?.percentiles?.p50?.seconds || t.stats?.avg_seconds || 0;
        const barWidth = Math.max((medianSeconds / maxMedian) * 100, 2);
        const barColor = getBarColor(medianSeconds, t.sla);
        const badge = getComplianceBadge(t.sla);
        const isSelected = selectedIndex === i;

        return (
          <div key={i} className={`duration-transition${isSelected ? ' duration-transition-selected' : ''}`}
            onClick={() => onSelect && onSelect(i)}
            style={{ cursor: onSelect ? 'pointer' : 'default' }}>
            <div className="duration-bar-row">
              <div className="duration-bar-label">
                <span style={{ fontWeight: '600', fontSize: '13px' }}>{t.label}</span>
                {!compact && (
                  <span style={{ fontSize: '11px', color: 'var(--text-tertiary)' }}>
                    {t.pair_count} entities
                    {t.sla && (t.sla.warning_seconds || t.sla.breach_seconds) && (
                      <> · SLA{t.sla.warning_seconds ? ` ⚠ ${formatDuration(t.sla.warning_seconds)}` : ''}{t.sla.breach_seconds ? ` ✕ ${formatDuration(t.sla.breach_seconds)}` : ''}</>
                    )}
                  </span>
                )}
              </div>
              <div className="duration-bar-container">
                <div className="duration-bar-fill" style={{ width: `${barWidth}%`, background: barColor }}>
                  <span className="duration-bar-value">
                    {t.stats?.percentiles?.p50?.formatted || t.stats?.avg || '—'}
                  </span>
                </div>
                {/* Threshold markers */}
                {t.sla?.warning_seconds && (
                  <div className="duration-bar-marker duration-bar-marker-warning"
                    style={{ left: `${Math.min((t.sla.warning_seconds / maxMedian) * 100, 100)}%` }}
                    title={`Warning: ${formatDuration(t.sla.warning_seconds)}`} />
                )}
                {t.sla?.breach_seconds && (
                  <div className="duration-bar-marker duration-bar-marker-breach"
                    style={{ left: `${Math.min((t.sla.breach_seconds / maxMedian) * 100, 100)}%` }}
                    title={`Breach: ${formatDuration(t.sla.breach_seconds)}`} />
                )}
              </div>
              {badge && (
                <span className="sla-badge" style={{ color: badge.color, borderColor: badge.color }}>
                  {badge.pct}% SLA
                </span>
              )}
            </div>

            {/* Compact stats row */}
            {!compact && t.stats && (
              <div className="duration-stats-row">
                {t.stats.percentiles && Object.entries(t.stats.percentiles).map(([key, val]) => (
                  val && <span key={key} className="duration-stat">{key}: {val.formatted}</span>
                ))}
                <span className="duration-stat">min: {t.stats.min || '—'}</span>
                <span className="duration-stat">max: {t.stats.max || '—'}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Histogram mode — bar chart for a single transition */
function HistogramView({ transition }) {
  if (!transition?.histogram || transition.histogram.length === 0) {
    return <div style={{ fontSize: '13px', color: 'var(--text-secondary)', padding: '16px 0' }}>No histogram data available.</div>;
  }

  const sla = transition.sla || {};
  const chartData = transition.histogram.map(b => {
    const midpoint = (b.range_min + b.range_max) / 2;
    let fill = '#3b82f6'; // blue default
    if (sla.breach_seconds && midpoint > sla.breach_seconds) fill = '#ef4444';
    else if (sla.warning_seconds && midpoint > sla.warning_seconds) fill = '#f59e0b';
    else if (sla.warning_seconds || sla.breach_seconds) fill = '#22c55e';
    return {
      label: `${formatDuration(b.range_min)} – ${formatDuration(b.range_max)}`,
      count: b.count,
      fill
    };
  });

  return (
    <div className="analytics-timeseries">
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e5e7eb)" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={60} />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} label={{ value: 'Entities', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
          <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '6px', border: '1px solid var(--border, #e5e7eb)' }} />
          <Bar dataKey="count" name="Entities">
            {chartData.map((entry, i) => (
              <rect key={i} fill={entry.fill} />
            ))}
          </Bar>
          {sla.warning_seconds && (
            <ReferenceLine x={`${formatDuration(sla.warning_seconds)}`} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'Warning', fill: '#f59e0b', fontSize: 10 }} />
          )}
          {sla.breach_seconds && (
            <ReferenceLine x={`${formatDuration(sla.breach_seconds)}`} stroke="#ef4444" strokeDasharray="5 5" label={{ value: 'Breach', fill: '#ef4444', fontSize: 10 }} />
          )}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function DurationChart({ transitions, onSelect, selectedIndex, compact, chartType }) {
  if (chartType === 'histogram' && transitions?.length > 0) {
    const idx = selectedIndex != null ? selectedIndex : 0;
    return <HistogramView transition={transitions[idx]} />;
  }
  return <SummaryBars transitions={transitions} onSelect={onSelect} selectedIndex={selectedIndex} compact={compact} />;
}
